// internal-deps
import { DashOrderResponse } from "@swiggy-private/payment-types";
import { format } from "@swiggy-private/js-utils";
import {
    GlobalHeaderState,
    HTTP_METHODS,
    makeDownstreamApiCallDeprecated,
    MerchApiResponse,
    MerchCarouselProps,
    Response,
    SwgyHeaderKeysList,
    addAdditionalHeadersToPLAPI,
} from "@swiggy-private/checkout-core";
import { ApiResponse, unwrapResponseWithCode } from "@swiggy-private/core-utils";
import { Err, Ok, Result } from "@swiggy-private/js-fp";

//constants
import {
    CANCEL_BOOKING_URI,
    DOWNSTREAM_HOSTS,
    FETCH_BANNER_ENDPOINT,
    FETCH_BOOKING_DETAILS_ENDPOINT,
    FETCH_ORDER_DETAILS_ENDPOINT,
    SPLIT_BILL_ENDPOINT,
} from "@dineout-payment-success-ui/utils/Hosts";
import { SuccessPagePLResponse } from "@dineout-payment-success-ui/interfaces/TableReservation";
import {
    ISplitBillCommsSuccessData,
    SplitBillApiRequest,
    SplitBillApiResponse,
} from "@dineout-payment-success-ui/interfaces";

async function cancelBooking(orderId: string) {
    const response = unwrapResponseWithCode(
        (await makeDownstreamApiCallDeprecated<ApiResponse<{}>>({
            apiHost: DOWNSTREAM_HOSTS.DO_HOST,
            endpoint: format(CANCEL_BOOKING_URI, orderId || ""),
            options: {
                method: HTTP_METHODS.POST,
            },
        })) as ApiResponse,
    );
    return response.toPromise();
}

export function cancelBookingWrapper(orderId: string): Promise<Result<{}, Error | ApiResponse>> {
    return cancelBooking(orderId)
        .then((cancelBookingResponse) => {
            return Ok(cancelBookingResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}

// Non-PL Response
export async function getOrderInfo(orderId: string) {
    const response = await makeDownstreamApiCallDeprecated<DashOrderResponse>(
        {
            apiHost: DOWNSTREAM_HOSTS.DO_HOST,
            endpoint: format(FETCH_ORDER_DETAILS_ENDPOINT, orderId),
            options: {
                method: HTTP_METHODS.GET,
            },
        },
        true,
    );

    return response;
}

// PL Response
export async function getBookingDetailsResponse(orderId: string, subPage: string): Promise<SuccessPagePLResponse> {
    const response = (await makeDownstreamApiCallDeprecated<SuccessPagePLResponse>({
        apiHost: DOWNSTREAM_HOSTS.DISC_SWIGGY,
        endpoint: format(FETCH_BOOKING_DETAILS_ENDPOINT, orderId, subPage),
        options: {
            method: HTTP_METHODS.GET,
            headers: addAdditionalHeadersToPLAPI(),
        },
    })) as SuccessPagePLResponse;

    return response;
}

export async function getMerchBanners(): Promise<MerchCarouselProps | undefined> {
    const lat = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
    const lng = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);
    const response = (await makeDownstreamApiCallDeprecated<MerchApiResponse>(
        {
            apiHost: DOWNSTREAM_HOSTS.DISC_SWIGGY,
            endpoint: `${FETCH_BANNER_ENDPOINT}?lat=${lat}&lng=${lng}`,
            options: {
                method: HTTP_METHODS.GET,
                headers: addAdditionalHeadersToPLAPI(),
            },
        },
        true,
    )) as Response<MerchApiResponse>;
    if (response.statusCode == 200 && response.response.success) {
        const { cards } = response.response.success;
        if (cards?.length > 0) {
            return cards[0]?.card?.card;
        }
    } else {
        return undefined;
    }
}

export async function submitSplitBill(payload: SplitBillApiRequest): Promise<ISplitBillCommsSuccessData | null> {
    const tid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.tid);
    const token = GlobalHeaderState.getHeader(SwgyHeaderKeysList.token);

    return unwrapResponseWithCode(
        (await makeDownstreamApiCallDeprecated<SplitBillApiResponse>({
            apiHost: DOWNSTREAM_HOSTS.DO_HOST,
            endpoint: SPLIT_BILL_ENDPOINT,
            options: {
                method: HTTP_METHODS.POST,
                headers: {
                    "Content-Type": "application/json",
                    tid: tid || "",
                    token: token || "",
                },
                body: JSON.stringify(payload),
            },
        })) as ApiResponse<SplitBillApiResponse["data"]>,
    ).toPromise();
}
